// Typed wrappers for a company's document theme — its branding (go-pim
// migration 0012, #62).
//
// A SEPARATE SUB-RESOURCE, deliberately not fields on the company payload.
// PUT /v1/companies/:id is a full-row replace, and a client that did not send
// the theme fields would blank a merchant's branding on the next unrelated save.
//
// THE THREE THINGS A READER GETS WRONG HERE:
//
//   1. The toggles are HIDE, not SHOW, all the way down to the column. The
//      polarity is preserved end to end on purpose: translating to show_*
//      anywhere is one negation away from blanking the bank block on every
//      document a company issues, and nothing upstream of the render notices.
//      If a checkbox reads "Show bank details", invert at the checkbox and
//      nowhere else.
//   2. The theme is FROZEN onto each document at issue. Editing it here never
//      restyles a document already issued — a merchant who rebrands does not
//      rewrite a receipt someone already filed with their taxes. Say so in the
//      UI, or an operator will believe a fix here repairs documents already sent.
//   3. accent_color is the ONE value that reaches a <style> block rather than
//      escaped text. go-pim rejects anything that is not exactly #RRGGBB, at
//      save AND again at render. Do not "helpfully" accept `red` or `#fff` here
//      and expand it — the panel does not get to widen that grammar.
import { apiClient } from "./client";

/** Server-side caps (go-pim internal/storage/themes.go), counted in RUNES. */
export const MAX_THEME_TITLE_LEN = 120;
export const MAX_THEME_SHORT_TEXT_LEN = 500;
export const MAX_THEME_TERMS_LEN = 4000;

/**
 * The only accent shape go-pim accepts. Six hex digits after a hash — no
 * three-digit shorthand, no named colours, no functional notation. Not because
 * those are invalid CSS but because the narrowest grammar that does the job is
 * the one with no room for a parser surprise, and this value is interpolated
 * into a <style> block on a public page.
 */
export const ACCENT_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

export function isValidAccentColor(v: string): boolean {
  return ACCENT_COLOR_RE.test(v.trim());
}

export interface DocumentTheme {
  /** "" means "use the built-in". Otherwise exactly #RRGGBB. */
  accent_color: string;

  /** Per doc type: a merchant issuing both wants different titles on each. */
  title_receipt: string;
  title_invoice: string;
  title_payment_confirmation: string;

  footer_note: string;
  terms: string;
  declaration: string;
  signature_name: string;

  /** HIDE, not show. See the note at the top of this file. */
  hide_bank_details: boolean;
  hide_payment_trail: boolean;
  hide_contacts: boolean;
  hide_verify_link: boolean;
}

/** The all-defaults theme: what an unbranded company has, and what go-pim
 *  returns for one that has never saved. Every field is empty-or-false, which
 *  is exactly the state that renders today's document unchanged. */
export const EMPTY_THEME: DocumentTheme = {
  accent_color: "",
  title_receipt: "",
  title_invoice: "",
  title_payment_confirmation: "",
  footer_note: "",
  terms: "",
  declaration: "",
  signature_name: "",
  hide_bank_details: false,
  hide_payment_trail: false,
  hide_contacts: false,
  hide_verify_link: false,
};

/** GET the theme. A company that has never configured one is NOT a 404 —
 *  go-pim returns the all-defaults theme, so there is no empty state to code. */
export async function getTheme(companyId: string): Promise<DocumentTheme> {
  const resp = await apiClient.get<DocumentTheme>(
    `/v1/companies/${companyId}/theme`,
  );
  return resp.data;
}

/** PUT replaces the whole theme. Safe here, unlike on the company row: this
 *  resource has one owner (the theme editor), every field is on the one form,
 *  and the panel GETs before it PUTs. */
export async function putTheme(
  companyId: string,
  theme: DocumentTheme,
): Promise<DocumentTheme> {
  const resp = await apiClient.put<DocumentTheme>(
    `/v1/companies/${companyId}/theme`,
    theme,
  );
  return resp.data;
}

/** Suggested accents. A starting point, not a restriction — the field accepts
 *  any #RRGGBB. Chosen to stay legible as a title colour on white AND on the
 *  dark background the document CSS switches to. */
export const ACCENT_PRESETS: { value: string; label: string }[] = [
  { value: "#0a7c3f", label: "Green" },
  { value: "#1d4ed8", label: "Blue" },
  { value: "#b45309", label: "Amber" },
  { value: "#b91c1c", label: "Red" },
  { value: "#6d28d9", label: "Violet" },
  { value: "#0f766e", label: "Teal" },
];
